import React, { useContext, useEffect, useState } from 'react'
import { AppSettings } from '../../../../../config/app-settings'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Loader from '../../../../pages/loader'
import {
  useGetUgcSearchLinksQuery,
  useMarkUgcSearchMutation
} from '../../../../../redux/apis/linkManagement/ugc/lMUgcSearchApiSlice'
import {
  Card,
  CardBody,
  CardFooter
} from '../../../../../components/other/card/card'
import ZoomableImageViewer from '../../../../../components/reviewing/ZoomableImageViewer'

const UgcSearchCampaignsByDomainReview = () => {
  const context = useContext(AppSettings)
  const { id, domain } = useParams()
  const [current, setCurrent] = useState(0)
  const [markedLinks, setMarkedLinks] = useState([])

  const { data, isLoading, error } = useGetUgcSearchLinksQuery({
    expand: ['campaign'].join(','),
    campaign: id,
    domain: domain,
    reviewed:false,
    page_size: 200,
  })

  const [mark, {isLoading:markLoading}] = useMarkUgcSearchMutation()

  const results = data?.data?.results || []
  const currentRecord = results[current]
  const count = data?.data?.count

  const handleMark = (status, record) => {
    if (!record) return
    // functional update: two keypresses in the same commit can't lose a mark
    setMarkedLinks(prev =>
      prev.some(item => item.url === record.url)
        ? prev.map(item =>
            item.url === record.url ? { ...item, status } : item
          )
        : [...prev, { url: record.url, status }]
    )

    if (current < results.length - 1) {
      switchTo(current + 1)
    } else {
      toast.info('Last link on this page — press Submit to load more')
    }
  }

  const handleSubmit = async () => {
    try {
      const response = await mark(markedLinks).unwrap()

      if (response?.success && response?.data) {
        const { marked_links, failed_links, errors } = response.data

        // Show success message
        if (marked_links > 0) {
          toast.success(response.message || `${marked_links} URL${marked_links > 1 ? 's' : ''} marked successfully`)
        }

        // Show error details if any links failed
        if (failed_links > 0 && errors) {
          Object.entries(errors).forEach(([url, error]) => {
            toast.error(`${url}: ${error}`)
          })
        }

        // Clear marked links and reset to first item
        setMarkedLinks([])
        setCurrent(0)
      } else {
        toast.error(response?.message || 'Submission failed')
      }
    } catch (err) {
      const errorMessage = err?.data?.message || err?.message || 'Failed to submit. Please try again.'
      toast.error(errorMessage)
    }
  }

  // ponytail: instant. The old 300ms spinner delay left currentRecord stale, so a
  // second keypress inside the window re-marked the previous link and skipped the next.
  const switchTo = setCurrent

  const handlePrevious = () => {
    if (current > 0) switchTo(current - 1)
  }

  const getButtonClass = (type) =>
    markedLinks.find(link => link.url === currentRecord?.url && link.status === type)
      ? 'active-status btn'
      : type === 'related'
      ? 'btn btn-theme'
      : type === 'unrelated'
      ? 'btn btn-default'
      : 'btn btn-info'

  useEffect(() => {
    if (error) toast.error('Failed to fetch links')
  }, [error])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!currentRecord) return
      if (e.repeat || e.ctrlKey || e.metaKey || e.altKey) return
      const tag = e.target?.tagName
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag) || e.target?.isContentEditable) return
      const key = e.key.toLowerCase()
      if (key === 'y') handleMark('related', currentRecord)
      if (key === 'n') handleMark('unrelated', currentRecord)
      if (key === 'v') handleMark('review_again', currentRecord)
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [currentRecord, markedLinks])

  useEffect(() => {
      if (data?.data?.count === 0) {
        toast.info('All links processed. Closing tab...')
        const timeout = setTimeout(() => {
          window.close()
        }, 1500)
  
        return () => clearTimeout(timeout)
      }
    }, [data?.data?.count])

  useEffect(() => {
    context.setAppHeaderNone(false)
    context.setAppSidebarNone(false)
    context.setAppContentClass('')
    context.setAppThemePanal(true)

    return () => {
      context.setAppHeaderNone(true)
      context.setAppSidebarNone(true)
      context.setAppContentClass('p-0')
      context.setAppThemePanal(false)
    }
    // ponytail: [] not [context] — AppSettings.Provider makes a new value object every
    // render, so [context] re-ran this effect forever (header true/false render loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className='w-full flex flex-col gap-4'>
      <ul className='breadcrumb'>
        <li className='breadcrumb-item'>
          <p className='text-[#249d79] font-extrabold'>UGC SEARCH LEVEL</p>
        </li>
        <li className='breadcrumb-item active'>
          {currentRecord?.campaign?.campaign_name?.toUpperCase()}
        </li>
      </ul>

      <Card>
        <CardBody className='h-[70vh] overflow-hidden'>
          <div className='flex flex-col md:flex-row gap-4 h-full'>
            <div className='flex flex-col gap-4 w-full md:w-1/2 overflow-auto p-2'>
              <div className='flex gap-1'>
                <p className='font-bold'>URL:</p>
                <a
                  href={currentRecord?.url}
                  target='_blank'
                  rel='noreferrer'
                  className='underline break-all'
                >
                  {currentRecord?.url}
                </a>
              </div>
              <div className='flex gap-1'>
                <p className='font-bold'>Campaign Name:</p>
                <p>{currentRecord?.campaign?.campaign_name?.toUpperCase()}</p>
              </div>
              {currentRecord?.campaign?.sample_of_work && (
                <div className='flex gap-1'>
                  <p className='font-bold'>Sample of Work:</p>
                  <a
                    href={currentRecord.campaign.sample_of_work}
                    target='_blank'
                    rel='noreferrer'
                    className='underline break-all'
                  >
                    {currentRecord.campaign.sample_of_work}
                  </a>
                </div>
              )}
              {currentRecord?.campaign?.sample_of_pirated_url && (
                <div className='flex gap-1'>
                  <p className='font-bold'>Sample of Pirated URL:</p>
                  <a
                    href={currentRecord.campaign.sample_of_pirated_url}
                    target='_blank'
                    rel='noreferrer'
                    className='underline break-all'
                  >
                    {currentRecord.campaign.sample_of_pirated_url}
                  </a>
                </div>
              )}

              <div className='flex gap-2 flex-wrap mt-auto'>
                <button
                  className={getButtonClass('related')}
                  onClick={() => handleMark('related', currentRecord)}
                >
                  Related (Y)
                </button>
                <button
                  className={getButtonClass('unrelated')}
                  onClick={() => handleMark('unrelated', currentRecord)}
                >
                  Unrelated (N)
                </button>
                <button
                  className={getButtonClass('review_again')}
                  onClick={() => handleMark('review_again', currentRecord)}
                >
                  Review Again (V)
                </button>
              </div>
            </div>

            <div className='w-full md:w-1/2 h-full overflow-y-auto p-2 border-l'>
              {/* ponytail: dropped ?Date.now() — it changed on every render, so the
                  screenshot re-downloaded and flickered each time a link was marked */}
              <ZoomableImageViewer
                src={`${
                  process.env.REACT_APP_CLOUDFRONT_URL
                }public/media/${currentRecord?.screenshot_path?.replace(
                  /^\/+/,
                  ''
                )}`}
                containerClass='w-full h-full p-2 border-l'
              />
            </div>
          </div>
        </CardBody>

        <CardFooter>
          <div className='flex gap-2 justify-between flex-wrap'>
            <div className='flex gap-2 items-center'>
              <button
                className='btn btn-theme'
                disabled={current === 0}
                onClick={handlePrevious}
              >
                Previous
              </button>
              <button
                className='btn btn-default'
                disabled={current === results.length - 1}
                onClick={() => switchTo(current + 1)}
              >
                Next
              </button>
              <p className='my-auto'>
                {current + 1}/{results.length} out of {count}
              </p>
            </div>

            {markedLinks.length > 0 && (
              <button className='btn btn-success' onClick={handleSubmit}>
                Submit ({markedLinks.length}/{count})
              </button>
            )}
          </div>
        </CardFooter>
      </Card>

      <Loader isLoading={isLoading || markLoading} />
    </div>
  )
}

export default UgcSearchCampaignsByDomainReview
